const THOUSAND = 1e3;
const MILLION = 1_000_000;
const BILLION = 1_000_000_000;

const numberFormatterCache = new Map<string, Intl.NumberFormat>();

function normalizeLocale(locale: Intl.LocalesArgument | undefined): string {
  if (!locale) {
    return 'default';
  }
  if (typeof locale === 'string') {
    return locale;
  }
  if (Array.isArray(locale)) {
    return locale.map((entry) => (typeof entry === 'string' ? entry : String(entry))).join(',');
  }
  // Handle Intl.Locale object
  return String(locale);
}

function createOptionsCacheKey(intlOptions?: Intl.NumberFormatOptions): string {
  if (!intlOptions) {
    return '';
  }
  const entries = Object.entries(intlOptions);
  entries.sort(([keyA], [keyB]) => keyA.localeCompare(keyB));
  return JSON.stringify(entries);
}

/**
 * Return a memoized Intl.NumberFormat for the given locale/options pair.
 */
export function getNumberFormatter(
  locale: Intl.LocalesArgument = 'en-US',
  intlOptions?: Intl.NumberFormatOptions
): Intl.NumberFormat {
  const cacheKey = `${normalizeLocale(locale)}|${createOptionsCacheKey(intlOptions)}`;
  const cached = numberFormatterCache.get(cacheKey);
  if (cached) {
    return cached;
  }
  const formatter = new Intl.NumberFormat(locale, intlOptions);
  numberFormatterCache.set(cacheKey, formatter);
  return formatter;
}

export type CompactLargeNumberStrategy = 'floor' | 'precision';

export interface FormatCompactNumberOptions {
  billionPrecision?: number;
  dropTrailingZeros?: boolean;
  enableBillions?: boolean;
  largeNumberStrategy?: CompactLargeNumberStrategy;
  millionPrecision?: number;
  thousandPrecision?: number;
}

export interface FormatPercentOptions {
  fallback?: string;
  fractionDigits?: number;
}

interface ResolvedCompactOptions {
  billionPrecision: number;
  dropTrailingZeros: boolean;
  enableBillions: boolean;
  largeNumberStrategy: CompactLargeNumberStrategy;
  millionPrecision: number;
  thousandPrecision: number;
}

interface FormatSegmentParams {
  absValue: number;
  config: ResolvedCompactOptions;
  divisor: number;
  precision: number;
  suffix: string;
}

const DEFAULT_COMPACT_OPTIONS: ResolvedCompactOptions = {
  thousandPrecision: 1,
  millionPrecision: 1,
  billionPrecision: 1,
  dropTrailingZeros: true,
  largeNumberStrategy: 'floor',
  enableBillions: true,
};

/**
 * Format a numeric value using K/M/B suffixes with configurable rounding rules.
 */
export function formatCompactNumber(value: number | null | undefined, options?: FormatCompactNumberOptions): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '0';
  }

  const config: ResolvedCompactOptions = {
    ...DEFAULT_COMPACT_OPTIONS,
    ...options,
  };

  const sign = value < 0 ? '-' : '';
  const absValue = Math.abs(value);

  if (config.enableBillions && absValue >= BILLION) {
    return `${sign}${formatSegment({
      absValue,
      divisor: BILLION,
      precision: config.billionPrecision,
      suffix: 'B',
      config,
    })}`;
  }
  if (absValue >= MILLION) {
    return `${sign}${formatSegment({
      absValue,
      divisor: MILLION,
      precision: config.millionPrecision,
      suffix: 'M',
      config,
    })}`;
  }
  if (absValue >= THOUSAND) {
    return `${sign}${formatSegment({
      absValue,
      divisor: THOUSAND,
      precision: config.thousandPrecision,
      suffix: 'K',
      config,
    })}`;
  }
  return `${sign}${absValue.toString()}`;
}

function formatSegment({ absValue, divisor, precision, suffix, config }: FormatSegmentParams): string {
  if (precision < 0) {
    throw new Error('Precision must be a non-negative integer');
  }

  const scaled = absValue / divisor;

  if (config.largeNumberStrategy === 'floor' && scaled >= 10) {
    return `${Math.floor(scaled)}${suffix}`;
  }

  const rounded = roundToPrecision(scaled, precision);
  const formatted = formatWithTrailingPolicy(rounded, precision, config.dropTrailingZeros);
  return `${formatted}${suffix}`;
}

function roundToPrecision(value: number, precision: number): number {
  if (precision === 0) {
    return Math.round(value);
  }
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
}

function formatWithTrailingPolicy(value: number, precision: number, dropTrailingZeros: boolean): string {
  if (precision === 0) {
    return value.toFixed(0);
  }
  const fixed = value.toFixed(precision);
  if (!dropTrailingZeros) {
    return fixed;
  }
  return Number(fixed).toString();
}

/**
 * Format a numeric value with locale-aware thousands separators.
 */
export function formatLocaleNumber(
  value: number | null | undefined,
  locale: Intl.LocalesArgument = 'en-US',
  intlOptions?: Intl.NumberFormatOptions
): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '0';
  }

  return getNumberFormatter(locale, intlOptions).format(value);
}

/**
 * Format a percentage-style value with fixed fractional precision.
 */
export function formatPercent(
  value: number | string | null | undefined,
  { fractionDigits = 1, fallback = '0.0' }: FormatPercentOptions = {}
): string {
  if (value === null || value === undefined) {
    return fallback;
  }

  const numericValue = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numericValue)) {
    return fallback;
  }

  const digits = Math.max(0, fractionDigits);
  return numericValue.toFixed(digits);
}

export interface FormatPercentLabelOptions extends FormatPercentOptions {
  readonly invalidLabel?: string;
  readonly largeValueFractionDigits?: number;
  readonly smallValueFractionDigits?: number;
  readonly suffix?: string;
  readonly switchPrecisionThreshold?: number;
  readonly zeroLabel?: string;
}

const DEFAULT_PERCENT_SUFFIX = '%';

/**
 * Format a percentage for UI labels, switching precision based on magnitude.
 */
export function formatPercentLabel(
  value: number | string | null | undefined,
  {
    smallValueFractionDigits = 1,
    largeValueFractionDigits = 0,
    switchPrecisionThreshold = 10,
    invalidLabel = '—',
    zeroLabel = '0%',
    suffix = DEFAULT_PERCENT_SUFFIX,
    fallback,
  }: FormatPercentLabelOptions = {}
): string {
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric)) {
    return invalidLabel;
  }

  if (numeric <= 0) {
    return zeroLabel;
  }

  const fractionDigits =
    Math.abs(numeric) >= switchPrecisionThreshold ? largeValueFractionDigits : smallValueFractionDigits;

  const formatted = formatPercent(numeric, { fractionDigits, fallback: fallback ?? '0' });
  return `${formatted}${suffix}`;
}

/**
 * Compact formatter for social metric counts (likes, replies, etc.).
 */
export function formatMetricCount(value: number | null | undefined): string {
  return formatCompactNumber(value, { enableBillions: true });
}

export function formatWholeNumber(value: number | null | undefined, locale: Intl.LocalesArgument = 'en-US'): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '0';
  }
  return formatLocaleNumber(Math.round(value), locale);
}
